import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Search, Package, FolderTree, Layers, Archive, MoreHorizontal } from "lucide-react";
import { productFamilyAPI, productSubFamilyAPI, productAPI } from "@/lib/api";
import { toast } from "sonner";
import { BulkActionBar, useSelection } from "@/components/BulkActionBar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

function FamiliesTab({ onSelect }) {
  const [families, setFamilies] = useState([]);
  const [counts, setCounts] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", prefix: "", description: "" });
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = useCallback(() => { productFamilyAPI.list().then(r => setFamilies(r.data)).catch(() => {}); productAPI.counts().then(r => setCounts(r.data.by_family || {})).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const selection = useSelection(families);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    try {
      if (editing) { await productFamilyAPI.update(editing.id, form); toast.success("Famille modifiée"); }
      else { await productFamilyAPI.create(form); toast.success("Famille créée"); }
      setShowDialog(false); setEditing(null); setForm({ name: "", prefix: "", description: "" }); load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette famille ?")) return;
    try {
      await productFamilyAPI.delete(id);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  const bulkDelete = async () => {
    if (!selection.selectedIds.length) return;
    if (!window.confirm(`Supprimer ${selection.count} famille(s) ? Les familles rattachees a des produits ou sous-familles seront bloquees.`)) return;
    setBulkLoading(true);
    try {
      const res = await productFamilyAPI.bulkDelete(selection.selectedIds);
      const { deleted = 0, blocked = [] } = res.data || {};
      if (deleted > 0) toast.success(`${deleted} famille(s) supprimee(s)`);
      if (blocked.length > 0) {
        const details = blocked.map(b => `${b.name} (${b.products || 0} produits, ${b.sub_families || 0} sous-familles)`).join("\n");
        toast.error(`${blocked.length} famille(s) bloquee(s) :\n${details}`);
      }
      selection.clear();
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.blocked) {
        const details = detail.blocked.map(b => `${b.name} (${b.products || 0} produits, ${b.sub_families || 0} sous-familles)`).join("\n");
        toast.error(`Aucune famille supprimee. Bloquees :\n${details}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur suppression");
      }
    } finally {
      setBulkLoading(false);
    }
  };

  const familyBulkActions = [
    { key: "delete", label: "Supprimer", icon: Trash2, variant: "outline", className: "text-red-600 border-red-200 hover:bg-red-50" },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
        <p className="text-sm text-slate-500">{families.length} famille(s)</p>
        <div className="flex items-center gap-3 flex-wrap">
          <BulkActionBar count={selection.count} actions={familyBulkActions} onAction={bulkDelete} disabled={bulkLoading} />
          <Button onClick={() => { setEditing(null); setForm({ name: "", prefix: "", description: "" }); setShowDialog(true); }} className="bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="add-family-btn">
            <Plus size={16} className="mr-2" /> Nouvelle famille
          </Button>
        </div>
      </div>
      <div className="responsive-table">
        <Table>
          <TableHeader><TableRow className="bg-slate-50"><TableHead className="w-10"><Checkbox checked={selection.isAllSelected} onCheckedChange={selection.toggleAll} data-testid="family-select-all" /></TableHead><TableHead className="uppercase text-xs text-slate-500">Nom</TableHead><TableHead className="uppercase text-xs text-slate-500">Prefixe</TableHead><TableHead className="uppercase text-xs text-slate-500">Description</TableHead><TableHead className="uppercase text-xs text-slate-500">Produits</TableHead><TableHead className="uppercase text-xs text-slate-500 w-24">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {families.map(f => (
              <TableRow key={f.id} className={`table-row-hover cursor-pointer ${selection.selected.has(f.id) ? "bg-indigo-50/50" : ""}`} onClick={() => onSelect?.(f.id)} data-testid={`family-row-${f.id}`}>
                <TableCell onClick={e => e.stopPropagation()}><Checkbox checked={selection.selected.has(f.id)} onCheckedChange={() => selection.toggle(f.id)} data-testid={`family-select-${f.id}`} /></TableCell>
                <TableCell className="font-medium">{f.name}</TableCell>
                <TableCell className="text-slate-500 font-mono text-sm">{f.prefix}</TableCell>
                <TableCell className="text-slate-500">{f.description}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-xs">{counts[f.id] || 0}</Badge></TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => { setEditing(f); setForm({ name: f.name, prefix: f.prefix || "", description: f.description || "" }); setShowDialog(true); }} data-testid={`edit-family-${f.id}`}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(f.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-family-${f.id}`}><Trash2 size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {families.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Aucune famille</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouvelle"} famille</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nom *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="family-name-input" /></div>
            <div><Label>Prefixe</Label><Input value={form.prefix} onChange={e => setForm({...form, prefix: e.target.value})} placeholder="Ex: LOG, MAT..." data-testid="family-prefix-input" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="family-desc-input" /></div>
            <Button onClick={save} className="w-full bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="save-family-btn">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubFamiliesTab({ onSelect }) {
  const [subFamilies, setSubFamilies] = useState([]);
  const [families, setFamilies] = useState([]);
  const [counts, setCounts] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ family_id: "", name: "", prefix: "", description: "" });
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = useCallback(() => {
    productSubFamilyAPI.list().then(r => setSubFamilies(r.data)).catch(() => {});
    productFamilyAPI.list().then(r => setFamilies(r.data)).catch(() => {});
    productAPI.counts().then(r => setCounts(r.data.by_sub_family || {})).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const selection = useSelection(subFamilies);

  const save = async () => {
    if (!form.name.trim() || !form.family_id) { toast.error("Nom et famille requis"); return; }
    try {
      if (editing) { await productSubFamilyAPI.update(editing.id, form); toast.success("Sous-famille modifiée"); }
      else { await productSubFamilyAPI.create(form); toast.success("Sous-famille créée"); }
      setShowDialog(false); setEditing(null); setForm({ family_id: "", name: "", prefix: "", description: "" }); load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette sous-famille ?")) return;
    try {
      await productSubFamilyAPI.delete(id);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  const bulkDelete = async () => {
    if (!selection.selectedIds.length) return;
    if (!window.confirm(`Supprimer ${selection.count} sous-famille(s) ? Celles rattachees a des produits seront bloquees.`)) return;
    setBulkLoading(true);
    try {
      const res = await productSubFamilyAPI.bulkDelete(selection.selectedIds);
      const { deleted = 0, blocked = [] } = res.data || {};
      if (deleted > 0) toast.success(`${deleted} sous-famille(s) supprimee(s)`);
      if (blocked.length > 0) {
        const details = blocked.map(b => `${b.name} (${b.products || 0} produits)`).join("\n");
        toast.error(`${blocked.length} sous-famille(s) bloquee(s) :\n${details}`);
      }
      selection.clear();
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.blocked) {
        const details = detail.blocked.map(b => `${b.name} (${b.products || 0} produits)`).join("\n");
        toast.error(`Aucune sous-famille supprimee. Bloquees :\n${details}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur suppression");
      }
    } finally {
      setBulkLoading(false);
    }
  };

  const sfBulkActions = [
    { key: "delete", label: "Supprimer", icon: Trash2, variant: "outline", className: "text-red-600 border-red-200 hover:bg-red-50" },
  ];

  const getFamilyName = (fid) => families.find(f => f.id === fid)?.name || "-";

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
        <p className="text-sm text-slate-500">{subFamilies.length} sous-famille(s)</p>
        <div className="flex items-center gap-3 flex-wrap">
          <BulkActionBar count={selection.count} actions={sfBulkActions} onAction={bulkDelete} disabled={bulkLoading} />
          <Button onClick={() => { setEditing(null); setForm({ family_id: "", name: "", prefix: "", description: "" }); setShowDialog(true); }} className="bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="add-sub-family-btn">
            <Plus size={16} className="mr-2" /> Nouvelle sous-famille
          </Button>
        </div>
      </div>
      <div className="responsive-table">
        <Table>
          <TableHeader><TableRow className="bg-slate-50"><TableHead className="w-10"><Checkbox checked={selection.isAllSelected} onCheckedChange={selection.toggleAll} data-testid="sub-family-select-all" /></TableHead><TableHead className="uppercase text-xs text-slate-500">Famille</TableHead><TableHead className="uppercase text-xs text-slate-500">Nom</TableHead><TableHead className="uppercase text-xs text-slate-500">Prefixe</TableHead><TableHead className="uppercase text-xs text-slate-500">Description</TableHead><TableHead className="uppercase text-xs text-slate-500">Produits</TableHead><TableHead className="uppercase text-xs text-slate-500 w-24">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {subFamilies.map(sf => (
              <TableRow key={sf.id} className={`table-row-hover cursor-pointer ${selection.selected.has(sf.id) ? "bg-indigo-50/50" : ""}`} onClick={() => onSelect?.(sf.family_id, sf.id)} data-testid={`sub-family-row-${sf.id}`}>
                <TableCell onClick={e => e.stopPropagation()}><Checkbox checked={selection.selected.has(sf.id)} onCheckedChange={() => selection.toggle(sf.id)} data-testid={`sub-family-select-${sf.id}`} /></TableCell>
                <TableCell><Badge variant="outline">{getFamilyName(sf.family_id)}</Badge></TableCell>
                <TableCell className="font-medium">{sf.name}</TableCell>
                <TableCell className="text-slate-500 font-mono text-sm">{sf.prefix}</TableCell>
                <TableCell className="text-slate-500">{sf.description}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-xs">{counts[sf.id] || 0}</Badge></TableCell>                <TableCell onClick={e => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => { setEditing(sf); setForm({ family_id: sf.family_id, name: sf.name, prefix: sf.prefix || "", description: sf.description || "" }); setShowDialog(true); }}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(sf.id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {subFamilies.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Aucune sous-famille</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouvelle"} sous-famille</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Famille *</Label>
              <Select value={form.family_id} onValueChange={v => setForm({...form, family_id: v})}>
                <SelectTrigger data-testid="sf-family-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>{families.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nom *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="sf-name-input" /></div>
            <div><Label>Prefixe</Label><Input value={form.prefix} onChange={e => setForm({...form, prefix: e.target.value})} placeholder="Ex: LOG, MAT..." data-testid="sf-prefix-input" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="sf-desc-input" /></div>
            <Button onClick={save} className="w-full bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="save-sf-btn">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProductsTab({ autoOpen, presetFamily, presetSubFamily }) {
  const [products, setProducts] = useState([]);
  const [families, setFamilies] = useState([]);
  const [subFamilies, setSubFamilies] = useState([]);
  const [search, setSearch] = useState("");
  const [filterFamily, setFilterFamily] = useState(presetFamily || "");
  const [filterSubFamily, setFilterSubFamily] = useState(presetSubFamily || "");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Apply preset filters when family/sub-family click changes props
  useEffect(() => {
    setFilterFamily(presetFamily || "");
    setFilterSubFamily(presetSubFamily || "");
    setPage(1);
  }, [presetFamily, presetSubFamily]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", reference: "", family_id: "", sub_family_id: "", description: "", unit_price: 0, purchase_price: 0, tva_rate: 20, has_sizes: false, sizes: [], has_colors: false, colors: [], stock: 0 });
  const [sizesInput, setSizesInput] = useState("");
  const [colorsInput, setColorsInput] = useState("");

  const load = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (filterFamily) params.family_id = filterFamily;
    if (filterSubFamily) params.sub_family_id = filterSubFamily;
    params.limit = 5000;
    productAPI.list(params).then(r => setProducts(r.data.items || r.data)).catch(() => {});
    productFamilyAPI.list().then(r => setFamilies(r.data)).catch(() => {});
    productSubFamilyAPI.list().then(r => setSubFamilies(r.data)).catch(() => {});
  }, [search, filterFamily, filterSubFamily]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (autoOpen) { openNew(); } }, [autoOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, unit_price: p.unit_price || 0, purchase_price: p.purchase_price || 0, tva_rate: p.tva_rate || 20, stock: p.stock || 0 });
    setSizesInput((p.sizes || []).join(", "));
    setColorsInput((p.colors || []).join(", "));
    setShowDialog(true);
  };

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", reference: "", family_id: "", sub_family_id: "", description: "", unit_price: 0, purchase_price: 0, tva_rate: 20, has_sizes: false, sizes: [], has_colors: false, colors: [], stock: 0 });
    setSizesInput(""); setColorsInput("");
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    const data = { ...form, sizes: form.has_sizes ? sizesInput.split(",").map(s => s.trim()).filter(Boolean) : [], colors: form.has_colors ? colorsInput.split(",").map(s => s.trim()).filter(Boolean) : [] };
    try {
      if (editing) { await productAPI.update(editing.id, data); toast.success("Produit modifié"); }
      else { await productAPI.create(data); toast.success("Produit créé"); }
      setShowDialog(false); load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await productAPI.delete(id); toast.success("Supprimé"); load();
  };

  // Helpers for name lookups
  const famMap = Object.fromEntries(families.map(f => [f.id, f]));
  const sfMap = Object.fromEntries(subFamilies.map(sf => [sf.id, sf]));
  const getFamName = (id) => famMap[id]?.name || "";
  const getSfName = (id) => sfMap[id]?.name || "";

  // Filtered sub-families for the form dialog
  const filteredSF = subFamilies.filter(sf => !form.family_id || sf.family_id === form.family_id);
  // Filtered sub-families for the filter dropdown
  const filterableSF = subFamilies.filter(sf => !filterFamily || sf.family_id === filterFamily);

  // Paginate products (filtering is done server-side)
  const filtered = products;
  const effPageSize = pageSize === "all" ? Math.max(filtered.length, 1) : pageSize;
  const totalPages = Math.max(1, Math.ceil(filtered.length / effPageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = pageSize === "all" ? filtered : filtered.slice((currentPage - 1) * effPageSize, currentPage * effPageSize);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [filterFamily, filterSubFamily, search, pageSize]);

  const selection = useSelection(paginated);
  const [bulkLoading, setBulkLoading] = useState(false);

  const handleBulkAction = async (actionKey) => {
    if (!selection.selectedIds.length) return;
    if (actionKey === "delete" && !window.confirm(`Supprimer ${selection.count} produit(s) ?`)) return;
    setBulkLoading(true);
    try {
      const res = await productAPI.bulkAction(selection.selectedIds, actionKey);
      toast.success(res.data.message);
      selection.clear();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur action groupée"); }
    finally { setBulkLoading(false); }
  };

  const productBulkActions = [
    { key: "delete", label: "Supprimer", icon: Trash2, variant: "outline", className: "text-red-600 border-red-200 hover:bg-red-50" },
    { key: "archive", label: "Archiver", icon: Archive, variant: "outline", className: "text-orange-600 border-orange-200 hover:bg-orange-50" },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-56">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="product-search" />
          </div>
          <Select value={filterFamily || "all"} onValueChange={v => { setFilterFamily(v === "all" ? "" : v); setFilterSubFamily(""); }}>
            <SelectTrigger className="w-full sm:w-44" data-testid="product-filter-family"><SelectValue placeholder="Famille" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes familles</SelectItem>
              {families.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterSubFamily || "all"} onValueChange={v => setFilterSubFamily(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-44" data-testid="product-filter-sf"><SelectValue placeholder="Sous-famille" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes sous-familles</SelectItem>
              {filterableSF.map(sf => <SelectItem key={sf.id} value={sf.id}>{sf.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="add-product-btn">
          <Plus size={16} className="mr-2" /> Nouveau produit
        </Button>
      </div>
      <div className="mb-4 flex items-center justify-between">
        <BulkActionBar count={selection.count} actions={productBulkActions} onAction={handleBulkAction} disabled={bulkLoading} />
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-400">Afficher</span>
            <select
              value={String(pageSize)}
              onChange={e => setPageSize(e.target.value === "all" ? "all" : parseInt(e.target.value))}
              className="h-7 text-xs border border-slate-200 rounded px-1.5 bg-white cursor-pointer"
              data-testid="product-page-size"
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="all">Tous</option>
            </select>
          </div>
          <span className="text-xs text-slate-400">{filtered.length} produit(s)</span>
        </div>
      </div>
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 px-1 gap-2">
          <span className="text-xs text-slate-500">Page {currentPage}/{totalPages}</span>
          <div className="flex gap-1 flex-wrap">
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={currentPage <= 1} onClick={() => setPage(p => p - 1)}>Precedent</Button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum;
              if (totalPages <= 7) pageNum = i + 1;
              else if (currentPage <= 4) pageNum = i + 1;
              else if (currentPage >= totalPages - 3) pageNum = totalPages - 6 + i;
              else pageNum = currentPage - 3 + i;
              return (
                <Button key={pageNum} variant={pageNum === currentPage ? "default" : "outline"} size="sm" className={`h-7 w-7 text-xs ${pageNum === currentPage ? "bg-slate-900 text-white" : ""}`} onClick={() => setPage(pageNum)}>
                  {pageNum}
                </Button>
              );
            })}
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={currentPage >= totalPages} onClick={() => setPage(p => p + 1)}>Suivant</Button>
          </div>
        </div>
      )}
      <div className="responsive-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-10 hidden md:table-cell"><Checkbox checked={selection.isAllSelected} onCheckedChange={selection.toggleAll} data-testid="product-select-all" /></TableHead>
              <TableHead className="uppercase text-xs text-slate-500">Nom</TableHead>
              <TableHead className="uppercase text-xs text-slate-500 hidden lg:table-cell">Référence</TableHead>
              <TableHead className="uppercase text-xs text-slate-500 hidden xl:table-cell">Famille</TableHead>
              <TableHead className="uppercase text-xs text-slate-500 hidden xl:table-cell">Sous-famille</TableHead>
              <TableHead className="uppercase text-xs text-slate-500">Prix vente</TableHead>
              <TableHead className="uppercase text-xs text-slate-500 hidden lg:table-cell">Prix achat</TableHead>
              <TableHead className="uppercase text-xs text-slate-500 hidden lg:table-cell">Marge</TableHead>
              <TableHead className="uppercase text-xs text-slate-500 hidden lg:table-cell">TVA</TableHead>
              <TableHead className="uppercase text-xs text-slate-500 hidden xl:table-cell">Stock</TableHead>
              <TableHead className="uppercase text-xs text-slate-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginated.map(p => (
              <TableRow key={p.id} className={`table-row-hover ${selection.selected.has(p.id) ? "bg-indigo-50/50" : ""}`}>
                <TableCell className="hidden md:table-cell" onClick={e => e.stopPropagation()}><Checkbox checked={selection.selected.has(p.id)} onCheckedChange={() => selection.toggle(p.id)} data-testid={`product-select-${p.id}`} /></TableCell>
                <TableCell className="font-medium text-xs sm:text-sm">{p.name}</TableCell>
                <TableCell className="text-slate-500 font-mono text-sm hidden lg:table-cell">{p.reference || "-"}</TableCell>
                <TableCell className="text-xs text-slate-600 hidden xl:table-cell">{getFamName(p.family_id) || "-"}</TableCell>
                <TableCell className="text-xs text-slate-600 hidden xl:table-cell">{getSfName(p.sub_family_id) || "-"}</TableCell>
                <TableCell className="money-value text-xs sm:text-sm">{formatEur(p.unit_price)}</TableCell>
                <TableCell className="money-value text-slate-500 hidden lg:table-cell">{p.purchase_price ? formatEur(p.purchase_price) : "-"}</TableCell>
                <TableCell className="font-mono text-sm hidden lg:table-cell">{p.purchase_price && p.unit_price ? `${(((p.unit_price - p.purchase_price) / p.unit_price) * 100).toFixed(1)}%` : "-"}</TableCell>
                <TableCell className="font-mono text-sm hidden lg:table-cell">{p.tva_rate}%</TableCell>
                <TableCell className="font-mono text-sm hidden xl:table-cell">{p.stock}</TableCell>
                <TableCell>
                  <div className="hidden md:flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(p.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-product-${p.id}`}><Trash2 size={14} /></Button>
                  </div>
                  <div className="md:hidden">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal size={16} /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => openEdit(p)}><Pencil size={12} className="mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem className="text-xs cursor-pointer text-red-600" onClick={() => remove(p.id)}><Trash2 size={12} className="mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginated.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-8">Aucun produit</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-4 px-1 gap-2">
          <span className="text-xs text-slate-500">Page {currentPage}/{totalPages}</span>
          <div className="flex gap-1 flex-wrap">
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={currentPage <= 1} onClick={() => setPage(p => p - 1)} data-testid="product-prev-page">Precedent</Button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum;
              if (totalPages <= 7) pageNum = i + 1;
              else if (currentPage <= 4) pageNum = i + 1;
              else if (currentPage >= totalPages - 3) pageNum = totalPages - 6 + i;
              else pageNum = currentPage - 3 + i;
              return (
                <Button key={pageNum} variant={pageNum === currentPage ? "default" : "outline"} size="sm" className={`h-7 w-7 text-xs ${pageNum === currentPage ? "bg-slate-900 text-white" : ""}`} onClick={() => setPage(pageNum)} data-testid={`product-page-${pageNum}`}>
                  {pageNum}
                </Button>
              );
            })}
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={currentPage >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="product-next-page">Suivant</Button>
          </div>
        </div>
      )}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouveau"} produit</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Nom *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="product-name-input" /></div>
              <div><Label>Référence (auto)</Label><Input value={form.reference} readOnly disabled className="bg-slate-50 text-slate-400" data-testid="product-ref-input" /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Famille</Label>
                <Select value={form.family_id} onValueChange={v => setForm({...form, family_id: v, sub_family_id: ""})}>
                  <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>{families.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sous-famille</Label>
                <Select value={form.sub_family_id} onValueChange={v => setForm({...form, sub_family_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>{filteredSF.map(sf => <SelectItem key={sf.id} value={sf.id}>{sf.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Prix de vente HT</Label><Input type="number" step="0.01" value={form.unit_price} onChange={e => setForm({...form, unit_price: parseFloat(e.target.value) || 0})} data-testid="product-price-input" /></div>
              <div><Label>Prix d'achat HT</Label><Input type="number" step="0.01" value={form.purchase_price} onChange={e => setForm({...form, purchase_price: parseFloat(e.target.value) || 0})} data-testid="product-purchase-price-input" /></div>
            </div>
            {form.purchase_price > 0 && form.unit_price > 0 && (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2.5 text-sm">
                <span className="text-emerald-700 font-medium">Marge directe : {formatEur(form.unit_price - form.purchase_price)} ({(((form.unit_price - form.purchase_price) / form.unit_price) * 100).toFixed(1)}%)</span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>TVA %</Label><Input type="number" step="0.1" value={form.tva_rate} onChange={e => setForm({...form, tva_rate: parseFloat(e.target.value) || 0})} /></div>
              <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={e => setForm({...form, stock: parseInt(e.target.value) || 0})} /></div>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-md">
              <Label>Gestion des tailles</Label>
              <Switch checked={form.has_sizes} onCheckedChange={v => setForm({...form, has_sizes: v})} data-testid="product-sizes-switch" />
            </div>
            {form.has_sizes && <div><Label>Tailles (separer par des virgules)</Label><Input placeholder="S, M, L, XL" value={sizesInput} onChange={e => setSizesInput(e.target.value)} data-testid="product-sizes-input" /></div>}
            <div className="flex items-center justify-between p-3 border rounded-md">
              <Label>Gestion des couleurs</Label>
              <Switch checked={form.has_colors} onCheckedChange={v => setForm({...form, has_colors: v})} data-testid="product-colors-switch" />
            </div>
            {form.has_colors && <div><Label>Couleurs (separer par des virgules)</Label><Input placeholder="Rouge, Bleu, Noir" value={colorsInput} onChange={e => setColorsInput(e.target.value)} data-testid="product-colors-input" /></div>}
            <Button onClick={save} className="w-full bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="save-product-btn">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ProductsPage() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const urlNew = searchParams.get("new");
  const [activeTab, setActiveTab] = useState(tabParam === "families" ? "families" : tabParam === "sub-families" ? "sub-families" : "products");
  const [presetFamily, setPresetFamily] = useState("");
  const [presetSubFamily, setPresetSubFamily] = useState("");

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t === "families") setActiveTab("families");
    else if (t === "sub-families") setActiveTab("sub-families");
    else setActiveTab("products");
  }, [searchParams]);

  const goToProducts = (familyId, subFamilyId) => {
    setPresetFamily(familyId || "");
    setPresetSubFamily(subFamilyId || "");
    setActiveTab("products");
  };

  return (
    <div className="page-container" data-testid="products-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Produits</h1>
        <p className="text-slate-500 mt-1">Gerez vos produits, familles et sous-familles</p>
      </div>
      <Card className="p-6 border border-slate-200 rounded-lg bg-white">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="products" data-testid="tab-products"><Package size={16} className="mr-2" />Produits</TabsTrigger>
            <TabsTrigger value="families" data-testid="tab-families"><FolderTree size={16} className="mr-2" />Familles</TabsTrigger>
            <TabsTrigger value="sub-families" data-testid="tab-sub-families"><Layers size={16} className="mr-2" />Sous-familles</TabsTrigger>
          </TabsList>
          <TabsContent value="products"><ProductsTab autoOpen={urlNew === "1"} presetFamily={presetFamily} presetSubFamily={presetSubFamily} /></TabsContent>
          <TabsContent value="families"><FamiliesTab onSelect={(fid) => goToProducts(fid, "")} /></TabsContent>
          <TabsContent value="sub-families"><SubFamiliesTab onSelect={(fid, sfid) => goToProducts(fid, sfid)} /></TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
